const SVG_NS = 'http://www.w3.org/2000/svg';

export type FloatingShapeKind = 'circle' | 'square' | 'star' | 'triangle';

export interface FloatingShapeOptions {
  top: string;
  left: string;
  size: number;
  color: string;
  shape: FloatingShapeKind;
  delaySeconds: number;
}

function svgEl(name: string, attrs: Record<string, string>): SVGElement {
  const el = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  return el;
}

export function createCloud(top: string, left: string, sizeRem: number, delaySeconds: number): SVGSVGElement {
  const root = document.createElementNS(SVG_NS, 'svg');
  root.setAttribute('viewBox', '0 0 100 60');
  root.setAttribute('class', 'decor-cloud');
  root.style.top = top;
  root.style.left = left;
  root.style.width = `${sizeRem}rem`;
  root.style.height = `${sizeRem * 0.6}rem`;
  root.style.animationDelay = `${delaySeconds}s`;

  root.appendChild(svgEl('ellipse', { cx: '30', cy: '40', rx: '22', ry: '18', fill: 'white', stroke: '#2A2154', 'stroke-width': '3' }));
  root.appendChild(svgEl('ellipse', { cx: '55', cy: '32', rx: '28', ry: '22', fill: 'white', stroke: '#2A2154', 'stroke-width': '3' }));
  root.appendChild(svgEl('ellipse', { cx: '78', cy: '42', rx: '18', ry: '14', fill: 'white', stroke: '#2A2154', 'stroke-width': '3' }));
  root.appendChild(svgEl('rect', { x: '8', y: '40', width: '84', height: '20', fill: 'white' }));
  root.appendChild(svgEl('line', { x1: '8', y1: '58', x2: '92', y2: '58', stroke: '#2A2154', 'stroke-width': '3' }));
  return root;
}

export function createClouds(): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'decor-clouds';
  wrap.appendChild(createCloud('6%', '10%', 7.5, 0));
  wrap.appendChild(createCloud('3%', '65%', 9.375, 2));
  wrap.appendChild(createCloud('20%', '40%', 5.625, 1));
  wrap.appendChild(createCloud('8%', '85%', 6.25, 3));
  return wrap;
}

export function createGrass(): SVGSVGElement {
  const root = document.createElementNS(SVG_NS, 'svg');
  root.setAttribute('viewBox', '0 0 1000 80');
  root.setAttribute('preserveAspectRatio', 'none');
  root.setAttribute('class', 'decor-grass');
  root.appendChild(
    svgEl('path', {
      d: 'M 0 80 L 0 40 Q 50 25 100 40 T 200 40 T 300 40 T 400 40 T 500 40 T 600 40 T 700 40 T 800 40 T 900 40 T 1000 40 L 1000 80 Z',
      fill: '#9BD96B',
      stroke: '#2A2154',
      'stroke-width': '4',
    }),
  );
  return root;
}

export function createFloatingShape(opts: FloatingShapeOptions): HTMLElement | SVGElement {
  if (opts.shape === 'circle' || opts.shape === 'square') {
    const el = document.createElement('div');
    el.className = `decor-float decor-float-${opts.shape}`;
    el.style.top = opts.top;
    el.style.left = opts.left;
    el.style.width = `${opts.size}rem`;
    el.style.height = `${opts.size}rem`;
    el.style.background = opts.color;
    el.style.animationDelay = `${opts.delaySeconds}s`;
    return el;
  }
  const root = document.createElementNS(SVG_NS, 'svg');
  root.setAttribute('viewBox', '0 0 100 100');
  root.setAttribute('class', `decor-float decor-float-${opts.shape}`);
  root.style.top = opts.top;
  root.style.left = opts.left;
  root.style.width = `${opts.size}rem`;
  root.style.height = `${opts.size}rem`;
  root.style.animationDelay = `${opts.delaySeconds}s`;
  if (opts.shape === 'triangle') {
    root.appendChild(svgEl('polygon', { points: '50,10 90,90 10,90', fill: opts.color, stroke: '#2A2154', 'stroke-width': '6', 'stroke-linejoin': 'round' }));
  } else {
    root.appendChild(
      svgEl('polygon', {
        points: '50,5 61,38 96,38 68,58 78,92 50,72 22,92 32,58 4,38 39,38',
        fill: opts.color,
        stroke: '#2A2154',
        'stroke-width': '6',
        'stroke-linejoin': 'round',
      }),
    );
  }
  return root;
}

export function createMenuFloatingShapes(): HTMLDivElement {
  const wrap = document.createElement('div');
  wrap.className = 'decor-floating';
  const config: FloatingShapeOptions[] = [
    { top: '8%', left: '6%', size: 4.375, color: '#34D1BF', shape: 'circle', delaySeconds: 0 },
    { top: '14%', left: '86%', size: 5, color: '#FFD23F', shape: 'star', delaySeconds: 0.4 },
    { top: '72%', left: '4%', size: 4.375, color: '#A06CD5', shape: 'square', delaySeconds: 0.8 },
    { top: '78%', left: '90%', size: 5, color: '#FF8E3C', shape: 'triangle', delaySeconds: 1.2 },
    { top: '40%', left: '2%', size: 3.125, color: '#9BD96B', shape: 'circle', delaySeconds: 1.6 },
  ];
  for (const c of config) wrap.appendChild(createFloatingShape(c));
  return wrap;
}

export function createDotsBg(): HTMLDivElement {
  const el = document.createElement('div');
  el.className = 'dots-bg';
  return el;
}
